/* global axios */
import ApiClient from '../ApiClient';

class WaCatalogAPI extends ApiClient {
  constructor() {
    super('omni/wa_catalogs', { accountScoped: true });
  }

  // ============================================
  // Catalogs
  // ============================================

  getCatalogs() {
    return axios.get(this.url);
  }

  getCatalog(catalogId) {
    return axios.get(`${this.url}/${catalogId}`);
  }

  createCatalog(data) {
    return axios.post(this.url, data);
  }

  updateCatalog(catalogId, data) {
    return axios.patch(`${this.url}/${catalogId}`, { wa_catalog: data });
  }

  deleteCatalog(catalogId) {
    return axios.delete(`${this.url}/${catalogId}`);
  }

  syncCatalog(catalogId, direction = 'to_meta') {
    return axios.post(`${this.url}/${catalogId}/sync`, { direction });
  }

  connectCatalog(catalogId, metaCatalogId) {
    return axios.post(`${this.url}/${catalogId}/connect`, {
      meta_catalog_id: metaCatalogId,
    });
  }

  // ============================================
  // Products
  // ============================================

  getProducts(catalogId, params = {}) {
    return axios.get(`${this.url}/${catalogId}/products`, { params });
  }

  getProduct(catalogId, productId) {
    return axios.get(`${this.url}/${catalogId}/products/${productId}`);
  }

  createProduct(catalogId, data) {
    const { blob_id, ...productData } = data;
    return axios.post(`${this.url}/${catalogId}/products`, {
      wa_catalog_product: productData,
      blob_id,
    });
  }

  updateProduct(catalogId, productId, data) {
    const { blob_id, ...productData } = data;
    return axios.patch(`${this.url}/${catalogId}/products/${productId}`, {
      wa_catalog_product: productData,
      blob_id,
    });
  }

  deleteProduct(catalogId, productId) {
    return axios.delete(`${this.url}/${catalogId}/products/${productId}`);
  }

  bulkUpdateProducts(catalogId, products) {
    return axios.patch(`${this.url}/${catalogId}/products/bulk_update`, {
      products,
    });
  }

  reorderProducts(catalogId, order) {
    return axios.patch(`${this.url}/${catalogId}/products/reorder`, { order });
  }

  syncProducts(catalogId) {
    return axios.post(`${this.url}/${catalogId}/products/sync`);
  }

  aiExtractProducts(catalogId, blobIds) {
    return axios.post(`${this.url}/${catalogId}/products/ai_extract`, {
      blob_ids: blobIds,
    });
  }

  bulkCreateProducts(catalogId, products) {
    return axios.post(`${this.url}/${catalogId}/products/bulk_create`, {
      products,
    });
  }

  // ============================================
  // Collections
  // ============================================

  getCollections(catalogId) {
    return axios.get(`${this.url}/${catalogId}/collections`);
  }

  getCollection(catalogId, collectionId) {
    return axios.get(
      `${this.url}/${catalogId}/collections/${collectionId}`
    );
  }

  createCollection(catalogId, data) {
    const { blob_id, ...collectionData } = data;
    return axios.post(`${this.url}/${catalogId}/collections`, {
      wa_catalog_collection: collectionData,
      blob_id,
    });
  }

  updateCollection(catalogId, collectionId, data) {
    const { blob_id, ...collectionData } = data;
    return axios.patch(
      `${this.url}/${catalogId}/collections/${collectionId}`,
      { wa_catalog_collection: collectionData, blob_id }
    );
  }

  deleteCollection(catalogId, collectionId) {
    return axios.delete(
      `${this.url}/${catalogId}/collections/${collectionId}`
    );
  }

  reorderCollections(catalogId, order) {
    return axios.patch(`${this.url}/${catalogId}/collections/reorder`, {
      order,
    });
  }

  syncCollections(catalogId) {
    return axios.post(`${this.url}/${catalogId}/collections/sync`);
  }

  // ============================================
  // Combos
  // ============================================

  getCombos(catalogId) {
    return axios.get(`${this.url}/${catalogId}/combos`);
  }

  getCombo(catalogId, comboId) {
    return axios.get(`${this.url}/${catalogId}/combos/${comboId}`);
  }

  createCombo(catalogId, data) {
    const { blob_id, ...comboData } = data;
    return axios.post(`${this.url}/${catalogId}/combos`, {
      ...comboData,
      blob_id,
    });
  }

  updateCombo(catalogId, comboId, data) {
    const { blob_id, ...comboData } = data;
    return axios.patch(`${this.url}/${catalogId}/combos/${comboId}`, {
      wa_catalog_combo: comboData,
      blob_id,
    });
  }

  deleteCombo(catalogId, comboId) {
    return axios.delete(`${this.url}/${catalogId}/combos/${comboId}`);
  }

  addComboItem(catalogId, comboId, productId, quantity = 1) {
    return axios.post(
      `${this.url}/${catalogId}/combos/${comboId}/add_item`,
      { product_id: productId, quantity }
    );
  }

  removeComboItem(catalogId, comboId, productId) {
    return axios.delete(
      `${this.url}/${catalogId}/combos/${comboId}/remove_item`,
      { data: { product_id: productId } }
    );
  }

  // ============================================
  // Modifier Groups
  // ============================================

  getModifierGroups(catalogId, productId) {
    return axios.get(
      `${this.url}/${catalogId}/products/${productId}/modifier_groups`
    );
  }

  createModifierGroup(catalogId, productId, data) {
    return axios.post(
      `${this.url}/${catalogId}/products/${productId}/modifier_groups`,
      data
    );
  }

  updateModifierGroup(catalogId, productId, groupId, data) {
    return axios.patch(
      `${this.url}/${catalogId}/products/${productId}/modifier_groups/${groupId}`,
      data
    );
  }

  deleteModifierGroup(catalogId, productId, groupId) {
    return axios.delete(
      `${this.url}/${catalogId}/products/${productId}/modifier_groups/${groupId}`
    );
  }

  // ============================================
  // Coupons
  // ============================================

  getCoupons(catalogId) {
    return axios.get(`${this.url}/${catalogId}/coupons`);
  }

  createCoupon(catalogId, data) {
    return axios.post(`${this.url}/${catalogId}/coupons`, data);
  }

  updateCoupon(catalogId, couponId, data) {
    return axios.patch(`${this.url}/${catalogId}/coupons/${couponId}`, data);
  }

  deleteCoupon(catalogId, couponId) {
    return axios.delete(`${this.url}/${catalogId}/coupons/${couponId}`);
  }

  toggleCouponActive(catalogId, couponId) {
    return axios.post(
      `${this.url}/${catalogId}/coupons/${couponId}/toggle_active`
    );
  }
}

export default new WaCatalogAPI();
